/**
 * Format: Custom variant list
 * -----------------------------------------------------------------------------
 * Formats custom metaobject response.
 *
 */
import { decodeApiId, transformEdges } from '~/helpers/graphql';
import { formatProduct } from '~/format/format-product';

/**
 * Format custom variant metaobject list.
 * - https://shopify.dev/docs/api/storefront/2023-10/objects/Metaobject
 * @param {Object} response - Metaobject response.
 * @returns {Object}
 */
export function formatCustomVariantList(response) {
  const references = response.metaobject?.fields
    .filter((field) => field.references !== null)
    .map((field) => field.references);

  if (!references.length) {
    return [];
  }

  const products = [];
  const variants = [];
  let productReference = {};
  let variantReference = {};
  let pageInfo = {};

  /**
   * Format products.
   */
  references.forEach((reference) => {
    transformEdges(reference).forEach((node) => {
      if (node.resourceType === 'ProductVariant') {
        variants.push(
          formatProduct(node.product, { variantId: decodeApiId(node.id) }),
        );
        variantReference = reference;
        return;
      }

      productReference = reference;
      products.push(formatProduct(node));
    });
  });

  /**
   * Combine so variants always come first.
   */
  const combined = [...variants, ...products];

  const allProducts = productReference?.edges?.length === combined.length;
  const allVariants = variantReference?.edges?.length === combined.length;

  /**
   * Determine if pagination is possible.
   * - If all products or all variants then use reference's pageInfo for
   * pagination purposes.
   * - Pagination isn't possible when there's a mix of results.
   */
  if (allProducts || allVariants) {
    const reference = allProducts ? productReference : variantReference;

    pageInfo = {
      ...reference?.pageInfo,
      currentPage: 1,
    };
  }

  /**
   * Return formatted data.
   */
  return {
    pageInfo,
    products: combined,
  };
}
